import { setThemeChoice, useThemeChoice, type ThemeChoice } from "../../lib/theme";
import { Group, PLATE } from "../shell/surfaces";

/**
 * Which skin, chosen in the mirror (PRD v3.3 §2.3a).
 *
 * It belongs in /you rather than in the shell for the same reason the door's
 * settings do: it is a fact about this person's setup, not a control they reach
 * for during a day's work. A theme toggle pinned to the chrome is a button
 * pressed twice in a lifetime occupying the same rank as the Inbox.
 *
 * TWO CONTROLS, NOT ONE, because there are two questions here and the reference
 * build asks them separately.
 *
 * The PICKER answers "how bright is the room": System, Light, Dark. A
 * radiogroup, because those three are mutually exclusive and a screen reader
 * should hear "2 of 3" rather than three switches that happen to disagree.
 * "System" is the important one and it is not a colour — it is the ABSENCE of a
 * choice, removing the attribute and letting `prefers-color-scheme` answer.
 * Without it the first tap would be irreversible, and on a laptop that switches
 * at sunset "whatever this machine is doing" is what most people actually want.
 *
 * The SWITCH answers something else entirely: do I want the doorman's own
 * paper-and-ink over the whole app. It is a switch and not a fourth segment
 * because it is not a fourth brightness — and because a segment reading "Nob"
 * beside "Light" and "Dark" invites the reader to parse it as one, which is the
 * mistake a previous pass made on the way to deleting the theme outright.
 *
 * While it is ON the picker DIMS but stays live. Dimming says "this is not what
 * you are looking at right now"; staying live is the way back out, and it is a
 * second way out — tapping any segment leaves nob mode, exactly as the switch
 * does. Underneath, all four states are one stored value (lib/theme.ts): a
 * reader cannot be in two rooms at once.
 *
 * The four PRINTED surfaces — the sign-in, both onboarding runs, the door and
 * the waiting room — ignore every control on this screen. They are printed for
 * everybody, in every theme (styles/poster.css).
 */

type Option = { value: ThemeChoice; label: string };

const OPTIONS: readonly Option[] = [
  { value: null, label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

export const THEME_NOTE =
  "System follows your machine, and picks light when it has no preference. " +
  "A choice here is remembered in this browser only.";

/** What the picker's caption says instead, once the brand skin has taken over. */
export const NOB_NOTE = "Nob mode is on. The rest of the world can stay beige.";

export const NOB_SWITCH_LABEL = "Nob mode";

/** The switch's own line, which does not change with its state. */
export const NOB_SWITCH_NOTE = "The yellow one. You found it.";

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

export function ThemeSwitcher() {
  const choice = useThemeChoice();
  const nob = choice === "nob";

  return (
    /* ONE PLATE, TWO ROWS — the shape the reference build gives this page and
       the one thing the earlier pass left out. The picker and its caption are a
       padded block at the top of the plate, the switch is a row under a
       hairline, and both are contained rather than floating on the panel's own
       ground. A page with two controls and nothing around them is exactly the
       loose text this restyle exists to end. */
    <Group>
      <div className={PLATE}>
        <div
          role="radiogroup"
          aria-label="Theme"
          className={`flex rounded-full bg-search p-0.5 transition-opacity duration-75
                      ${nob ? "opacity-50" : ""}`}
        >
          {OPTIONS.map((option) => {
            const active = choice === option.value;
            return (
              <button
                key={option.label}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setThemeChoice(option.value)}
                className={`min-w-0 flex-1 rounded-full px-2 py-1.5 text-[12px] font-medium
                            transition-colors duration-75 ${FOCUS} ${
                              active ? "bg-bg text-text shadow-sm" : "text-muted hover:text-text"
                            }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* `muted`, not `fainter`. This is a whole sentence a reader is meant to
            read, and `fainter` is the placeholder-and-disabled rung — under AA at
            any value it can honestly take, which styles/tokens.css already flags
            as a call-site problem rather than a rung to loudify. It is the colour
            the reference build sets this exact caption in, and it takes the line
            from 2.99:1 to 10.2:1 in nob mode and from 2.9:1 to 4.9:1 in light. */}
        <p className="mt-2 text-[11px] leading-snug text-muted">{nob ? NOB_NOTE : THEME_NOTE}</p>
      </div>

      {/* Full width inside the plate, so the hairline runs edge to edge and the
          row reads as a row — the way every other setting in this panel does.
          The negative-margin bleed this used to need is gone with the plate. */}
      <button
        type="button"
        role="switch"
        aria-checked={nob}
        aria-label={NOB_SWITCH_LABEL}
        onClick={() => setThemeChoice(nob ? null : "nob")}
        className={`flex w-full items-center justify-between gap-3 border-t border-border-soft
                    px-3 py-3 text-left transition-colors duration-75 hover:bg-hover ${FOCUS}`}
      >
        <span>
          <span className="block text-[13px] font-medium">{NOB_SWITCH_LABEL}</span>
          <span className="block text-[11px] leading-snug text-muted">{NOB_SWITCH_NOTE}</span>
        </span>
        {/* The track is decoration: the button above it already carries the
            role, the label and the state, so a screen reader that also read
            this would announce the control twice. */}
        <span className="nob-switch" data-on={nob ? "true" : "false"} aria-hidden="true">
          <i />
        </span>
      </button>
    </Group>
  );
}
